"use client";

import { usePusher } from "./PusherProvider";
import { useCallbackRef } from "@radix-ui/react-use-callback-ref";
import { useEffect, useRef } from "react";

export function useChannelEvent<T>(
  channelName: string,
  eventName: string,
  callback: (data: T, metadata?: { user_id: string }) => void
) {
  const { subscribe, unsubscribe } = usePusher();
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    const normalizedChannelName = channelName.replace(":", ".");
    if (!normalizedChannelName) {
      return;
    }
    const channel = subscribe(normalizedChannelName);
    const callbckWrapper = (data: T, metadata: { user_id: string }) => {
      callbackRef.current(data, metadata);
    };
    channel.bind(eventName, callbckWrapper);
    return () => {
      channel.unbind(eventName, callbckWrapper);
      unsubscribe(normalizedChannelName);
    };
  }, [channelName, eventName]);
}

export function useChannelEvents<T>(
  channelName?: string,
  eventNames?: string[],
  callback?: (
    eventName: string,
    data: T,
    metadata?: { user_id: string }
  ) => void
) {
  const { subscribe, unsubscribe } = usePusher();

  const callbackRef = useCallbackRef(callback);

  useEffect(() => {
    if (!channelName || !eventNames) {
      return;
    }
    const normalizedChannelName = channelName.replace(":", ".");
    if (!normalizedChannelName) {
      return;
    }
    const channel = subscribe(normalizedChannelName);
    const callbacks = eventNames.map((eventName) => {
      return {
        eventName,
        callback: (data: T, metadata: { user_id: string }) => {
          return callbackRef(eventName, data, metadata);
        },
      };
    });
    callbacks.forEach((cb) => {
      channel.bind(cb.eventName, cb.callback);
    });
    return () => {
      callbacks.forEach((cb) => {
        channel.unbind(cb.eventName, cb.callback);
      });
      unsubscribe(normalizedChannelName);
    };
  }, [channelName, ...(eventNames ?? []), subscribe, unsubscribe]);
}
